import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { http, formatApiError } from "@/lib/api";
import { formatDateTime, maskPhone, onlyDigits } from "@/lib/format";
import { Panel } from "@/components/admin/PageBits";
import { CustomActionButtons, ReviewButtons } from "@/components/admin/CustomActionButtons";
import { RedeemProgressControl } from "@/components/admin/RedeemProgress";

const CODE_STATUS = {
  AGUARDANDO: ["Aguardando confirmação do cliente", "text-amber-300"],
  VALIDANDO: ["Cliente informou o código correto: aguardando sua validação", "text-sky-300"],
  CONFIRMADO: ["Código confirmado pelo cliente", "text-emerald-300"],
  BLOQUEADO: ["Bloqueado: tentativas esgotadas", "text-red-300"],
};
const fieldCls = "border-white/10 bg-black/30 font-mono-x text-slate-100";

const CodeStatus = ({ cr, onReview }) => {
  const [label, cls] = CODE_STATUS[cr.status] || [cr.status, "text-slate-300"];
  return (
    <div className="mb-3 rounded-sm border border-white/10 bg-black/20 p-3 text-sm" data-testid="code-request-status">
      <p className={`font-semibold ${cls}`} data-testid="code-request-status-label">{label}</p>
      <p className="mt-1 text-xs text-slate-500">Tel. {maskPhone(cr.phone)} · {cr.attempts} tentativa(s) incorreta(s) · {formatDateTime(cr.requested_at)} · {cr.operator?.name}</p>
      {cr.status === "VALIDANDO" && <ReviewButtons onReview={onReview} testPrefix="code" />}
    </div>
  );
};

export const ActionsPanel = ({ req, phoneHint, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const cr = req.code_request;

  const openDialog = () => { setPhone(maskPhone(phoneHint || "")); setCode(""); setOpen(true); };

  const call = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setOpen(false); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const send = () => call(() => http.post(`/admin/requests/${req.id}/code/request`, { phone: onlyDigits(phone), code }), "Cliente avisado: tela de confirmação de código exibida.");
  const cancel = () => call(() => http.post(`/admin/requests/${req.id}/code/cancel`), "Solicitação de código cancelada.");
  const review = (approve) => call(() => http.post(`/admin/requests/${req.id}/code/review`, { approve }), approve ? "Código validado." : "Código recusado; o cliente enviará novamente.");

  return (
    <Panel title="Ações" testId="actions-panel">
      {req.status === "RESGATE_SOLICITADO" && <RedeemProgressControl req={req} onChanged={onChanged} />}
      {cr && <CodeStatus cr={cr} onReview={review} />}
      <div className="flex flex-wrap gap-2">
        <button onClick={openDialog} className="btn-ghost-sm" data-testid="request-code-btn"><MessageCircle className="h-3.5 w-3.5" /> {cr ? "Solicitar novo código" : "Solicitar código via WhatsApp"}</button>
        {cr && !["CONFIRMADO", "VALIDANDO"].includes(cr.status) && <button onClick={cancel} disabled={busy} className="btn-ghost-sm" data-testid="cancel-code-btn"><X className="h-3.5 w-3.5" /> Cancelar</button>}
      </div>
      <CustomActionButtons req={req} onChanged={onChanged} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-white/10 bg-[#141C2E] text-slate-100">
          <DialogHeader>
            <DialogTitle>Solicitar código via WhatsApp</DialogTitle>
            <DialogDescription className="text-slate-400">Envie o código pelo seu sistema de WhatsApp e informe abaixo o mesmo código. O cliente verá uma tela para digitá-lo e você acompanha aqui a confirmação.</DialogDescription>
          </DialogHeader>
          <label className="text-xs text-slate-400">Telefone (WhatsApp) com DDD</label>
          <Input value={phone} onChange={(e) => setPhone(maskPhone(e.target.value))} placeholder="(11) 91234-5678" className={fieldCls} data-testid="code-phone-input" />
          <label className="text-xs text-slate-400">Código enviado (6 dígitos)</label>
          <Input value={code} inputMode="numeric" onChange={(e) => setCode(onlyDigits(e.target.value).slice(0, 6))} placeholder="000000" className={`${fieldCls} tracking-[0.4em]`} data-testid="code-value-input" />
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-ghost-sm" data-testid="code-dialog-cancel">Cancelar</button>
            <button onClick={send} disabled={busy || code.length !== 6 || onlyDigits(phone).length < 10} className="btn-gold h-9 px-4 text-sm" data-testid="code-dialog-confirm">Avisar cliente</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
};
